import * as React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Alert,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material'

const VerificationCodeDialog = ({
  open,
  onClose,
  onConfirm,
  onRequestNew,
  loading,
  resourceLabel,
  error,
  resendCount,
  retryAfterSeconds,
}) => {
  const [code, setCode] = React.useState('')
  const [canResend, setCanResend] = React.useState(false)
  const [secondsLeft, setSecondsLeft] = React.useState(0)

  // Reset timer and input when a new code is sent (resendCount changes)
  React.useEffect(() => {
    const delay = (retryAfterSeconds ?? 60)
    setCanResend(false)
    setSecondsLeft(delay)
    setCode('')

    const interval = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(interval)
          setCanResend(true)
          return 0
        }
        return s - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [resendCount, retryAfterSeconds])

  const handleCodeChange = e => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 6)
    setCode(val)
  }

  const handleConfirm = () => {
    if (code.length < 6 || loading) return
    onConfirm(code)
  }

  const handleResend = () => {
    if (!canResend || loading) return
    onRequestNew()
  }

  const handleClose = (event, reason) => {
    // El modal debe persistir mientras el usuario va a su correo a leer el código:
    // no cerrar por clic fuera (backdrop) ni por Escape. Solo Cancelar o confirmar.
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return
    if (loading) return
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} disableEscapeKeyDown maxWidth="xs" fullWidth>
      <DialogTitle fontWeight={600} color="error.main">
        Verificar eliminación
      </DialogTitle>

      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Se envió un código de verificación a tu correo. Ingrésalo para confirmar la eliminación de{' '}
          <strong>{resourceLabel}</strong>.
        </DialogContentText>

        <TextField
          fullWidth
          label="Código de verificación"
          value={code}
          onChange={handleCodeChange}
          inputProps={{ inputMode: 'numeric', maxLength: 6 }}
          disabled={loading}
          sx={{
            mb: 1,
            '& input': {
              fontSize: '2rem',
              textAlign: 'center',
              letterSpacing: '0.25em',
              fontFamily: 'monospace',
            },
          }}
          autoFocus
          onKeyDown={e => { if (e.key === 'Enter') handleConfirm() }}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 1 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          {canResend ? (
            <Button size="small" onClick={handleResend} disabled={loading} variant="text">
              Reenviar código
            </Button>
          ) : (
            <Typography variant="caption" color="text.secondary">
              Reenviar en {secondsLeft}s
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancelar
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={code.length < 6 || loading}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}>
          Confirmar eliminación
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default VerificationCodeDialog
